import React, { useEffect, useState } from "react";
import { Box, Stack, Text, Separator } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Heading } from "@digital-services-gard-as/anchor-react";
import { InvoiceSenderDto, InvoiceFromOcrDto } from "@/api/invoiceApi";

import { ClaimsSelect } from "@/app/invoice/[invoiceId]/_components/createInvoice/InvoiceDetailsAndExpenses/generalInfo/claimsSelect";
import { BankAccountSelect } from "@/app/invoice/[invoiceId]/_components/createInvoice/InvoiceDetailsAndExpenses/generalInfo/bankAccountSelect";
import { AddressInfo } from "./AddressInfo";
import { GeneralInfoSkeleton } from "./GeneralInfoSkeleton";
import { BankAccountDto } from "@/types";

type GeneralInfoProps = {
    bankAccounts?: BankAccountDto[];
    onOpenBankDetails?: () => void;
    invoiceSender?: InvoiceSenderDto;
    missingBankAccount: string | undefined;
    invoiceFromOcr?: InvoiceFromOcrDto;
    crmId?: string;
    isLoadingOcr?: boolean;
    isLoadingClaim?: boolean;
};

export const GeneralInfo = React.memo(function GeneralInfo({
    bankAccounts,
    onOpenBankDetails,
    invoiceSender,
    missingBankAccount,
    invoiceFromOcr,
    crmId,
    isLoadingOcr,
    isLoadingClaim
}: GeneralInfoProps) {
    const { t } = useTranslation(["invoices", "errorMessages"]);

    // Claim data is refetched while the user interacts with the claim selector. Skeletonizing
    // then would unmount ClaimsSelect and discard its edit state, so only the first load counts.
    const [hasLoadedClaim, setHasLoadedClaim] = useState(!isLoadingClaim);
    useEffect(() => {
        if (!isLoadingClaim) {
            setHasLoadedClaim(true);
        }
    }, [isLoadingClaim]);

    const showSkeleton = Boolean(isLoadingOcr) || (Boolean(isLoadingClaim) && !hasLoadedClaim);

    return (
        <Box flex={{ base: "unset", lg: "1" }} minW={0} w="100%">
            <Heading size="lg" id="generalInfoTitle">
                {t("createInvoice.generalInfoTitle")}
            </Heading>
            <Box bg="white" p={{ base: "1rem", md: "1.5rem" }} borderRadius="md" boxShadow="xs" mt={4} minW={0}>
                <Stack gap={6}>
                    <Box
                        bg="#F5F7F8"
                        borderRadius="lg"
                        p={{ base: "1rem", md: "1.5rem" }}
                        boxShadow="xs"
                        css={{
                            "& *, &": { minWidth: 0 }
                        }}
                    >
                        <Stack gap={4}>
                            <AddressInfo crmId={crmId} />
                            <InfoItem
                                id="invoiceFrom"
                                label={t("invoices:createInvoice.generalInfo.invoiceFrom")}
                                value={invoiceSender?.name}
                            />
                        </Stack>
                    </Box>
                    {showSkeleton ? (
                        <GeneralInfoSkeleton />
                    ) : (
                        <>
                            <ClaimsSelect hasOcrResponse={Boolean(invoiceFromOcr)} crmId={crmId} />
                            <Separator />
                            <BankAccountSelect
                                bankAccounts={bankAccounts}
                                onOpenBankDetails={onOpenBankDetails}
                                missingBankAccount={missingBankAccount}
                                ocrResponse={invoiceFromOcr}
                            />
                        </>
                    )}
                </Stack>
            </Box>
        </Box>
    );
});

const InfoItem = ({ label, value, id }: { label: string; value?: string; id: string }) => (
    <Box>
        <Text mb="0.5rem" display="block" id={id} asChild>
            <b>{label}</b>
        </Text>
        <Text>{value ?? "—"}</Text>
    </Box>
);
